// Image Choice Question Component
// Handles user interaction for image-based trivia questions
// Displays images that users can click to select
// Follows the Section, Component & Manager System architecture

import React, { useState } from 'react';

export function ImageChoiceQuestion({
  trivia,
  selectedAnswer,
  showResults,
  onAnswerSelect,
  onSubmitAnswer,
  disabled = false
}) {
  const [imageErrors, setImageErrors] = useState({});


  const handleImageError = (optionIndex) => {
    setImageErrors(prev => ({
      ...prev,
      [optionIndex]: true
    }));
  };

  const options = trivia.questionData?.options || [];
  const displayMode = trivia.questionData?.displayMode || 'grid';

  return (
    <div className="learn-trivia-options">
      <div className="image-choice-container">
        <div className={`ic-gallery-grid ${displayMode}`}>
          {options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = trivia.questionData.correctAnswer === index;
            const hasError = imageErrors[index];

            return (
              <div
                key={index}
                className={`ic-gallery-item ${
                  isSelected ? 'selected' : ''
                } ${
                  showResults ? (
                    isCorrect ? 'correct' :
                    isSelected ? 'incorrect' : 'disabled'
                  ) : ''
                } ${hasError ? 'image-error' : ''}`}
                onClick={async () => {
                  if (showResults || disabled) return;
                  // For single-select questions, selecting immediately submits
                  onAnswerSelect(index); // Update selection state first
                  await onSubmitAnswer(index);
                }}
              >
                <div className="ic-image-container">
                  {hasError ? (
                    <div className="ic-error-placeholder">
                      <div className="ic-error-icon">
                        <i className="fa-solid fa-image-slash"></i>
                      </div>
                      <div className="ic-error-text">Image failed to load</div>
                    </div>
                  ) : (
                    <img
                      src={option.imageUrl}
                      alt={option.text || `Option ${String.fromCharCode(65 + index)}`}
                      className="ic-image"
                      onError={() => handleImageError(index)}
                      loading="lazy"
                    />
                  )}

                  {/* Selection overlay */}
                  <div className="ic-overlay">
                    {showResults && isCorrect && (
                      <div className="ic-correct-badge">
                        <i className="fa-solid fa-check"></i>
                      </div>
                    )}

                    {showResults && isSelected && !isCorrect && (
                      <div className="ic-incorrect-badge">
                        <i className="fa-solid fa-xmark"></i>
                      </div>
                    )}
                  </div>

                  {/* Label badge */}
                  <div className="ic-label-badge">
                    {String.fromCharCode(65 + index)}
                  </div>
                </div>

                {/* Caption */}
                {option.text && (
                  <div className="ic-caption">
                    {option.text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>


      {!showResults && (selectedAnswer === undefined || selectedAnswer === null) && (
        <div className="image-choice-hint">
          <small>Click on an image to select your answer</small>
        </div>
      )}

      {showResults && (
        <div className="learn-trivia-result">
          <div className="image-choice-results">
            <div className="result-summary">
              {selectedAnswer !== undefined && selectedAnswer !== null ? (
                <strong>Your selection: {String.fromCharCode(65 + selectedAnswer)}</strong>
              ) : (
                <strong>No answer selected</strong>
              )}
            </div>
          </div>
          <div className="learn-explanation">
            <strong>Explanation:</strong> {trivia.explanation}
          </div>
        </div>
      )}
    </div>
  );
}